import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Mail, Lock, User, AlertCircle } from 'lucide-react';
import { Button } from '@/components/Button.tsx';
import { Input } from '@/components/Input.tsx';
import { useAuth } from '@/hooks/useAuth.ts';
import type { ApiError } from '@/lib/api.ts';

export function RegisterPage() {
  const navigate = useNavigate();
  const { register, isRegistering, getOAuthUrl } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Basic client-side validation
    if (password.length < 8) {
      setFieldErrors({ password: 'Password must be at least 8 characters' });
      return;
    }

    try {
      await register({ name, email, password });
      // After registration, redirect to create first workspace
      navigate('/onboarding', { replace: true });
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.details && Array.isArray(apiError.details)) {
        const errors: Record<string, string> = {};
        for (const detail of apiError.details) {
          const d = detail as { path?: string; message?: string };
          if (d.path && d.message) {
            errors[d.path] = d.message;
          }
        }
        setFieldErrors(errors);
      } else {
        setError(apiError.message || 'Failed to create account. Please try again.');
      }
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Box className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">Create your account</h1>
          <p className="mt-1 text-sm text-foreground-muted">
            Start managing your 3D printing inventory
          </p>
        </div>

        {/* OAuth buttons */}
        <div className="space-y-3">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => (window.location.href = getOAuthUrl('google'))}
          >
            <GoogleIcon className="h-5 w-5" />
            Continue with Google
          </Button>
          {/* Temporarily hidden until Azure is configured */}
          {/* <Button
            variant="secondary"
            className="w-full"
            onClick={() => (window.location.href = getOAuthUrl('microsoft'))}
          >
            <MicrosoftIcon className="h-5 w-5" />
            Continue with Microsoft
          </Button> */}
        </div>

        {/* Divider */}
        <div className="my-6 flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-sm text-foreground-subtle">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md bg-error/10 p-3 text-sm text-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <Input
            label="Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            leftIcon={<User className="h-4 w-4" />}
            error={fieldErrors.name}
            required
            autoComplete="name"
            autoFocus
          />

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            leftIcon={<Mail className="h-4 w-4" />}
            error={fieldErrors.email}
            required
            autoComplete="email"
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            leftIcon={<Lock className="h-4 w-4" />}
            hint="At least 8 characters"
            error={fieldErrors.password}
            required
            autoComplete="new-password"
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={isRegistering}
          >
            Create account
          </Button>
        </form>

        {/* Terms */}
        <p className="mt-4 text-center text-xs text-foreground-subtle">
          By creating an account, you agree to our{' '}
          <a href="/terms" className="text-primary hover:text-primary-hover">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="text-primary hover:text-primary-hover">
            Privacy Policy
          </a>
        </p>

        {/* Login link */}
        <p className="mt-6 text-center text-sm text-foreground-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:text-primary-hover">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

// Temporarily commented out until Microsoft OAuth is configured
// function MicrosoftIcon({ className }: { className?: string }) {
//   return (
//     <svg className={className} viewBox="0 0 24 24">
//       <path fill="#f25022" d="M1 1h10v10H1z" />
//       <path fill="#00a4ef" d="M1 13h10v10H1z" />
//       <path fill="#7fba00" d="M13 1h10v10H13z" />
//       <path fill="#ffb900" d="M13 13h10v10H13z" />
//     </svg>
//   );
// }
